import { NAVIGATION_REGISTRY, type NavContext, type NavEntry } from "@/app/auth/navigation.registry";
import { findEntryByTo, isEntryVisible } from "@/app/auth/resolveNavAccess";

import { DASHBOARD_ALERT_TYPES, type DashboardAlertType } from "./dashboardAlertCatalog";

/**
 * The page each alert is about. An alert exists for a person exactly when the sidebar shows them that page,
 * so whatever hides the page hides its alert too, and the two can never disagree: the read permission,
 * tenancy, an organization's module allow-list (`allowed_menu_modules`) and the features staff turn on per
 * organization (batch payouts, say).
 */
export const DASHBOARD_ALERT_PAGE: Record<DashboardAlertType, string> = {
  payins_breb_key: "/app/payments-activity",
  payins_collections: "/app/payins",
  payouts_paid: "/app/dispersions",
  payouts_failed: "/app/dispersions",
  payout_batches_done: "/app/payout-batches",
  cards_purchase_approved: "/app/cards/cards",
  cards_purchase_declined: "/app/cards/cards",
  cards_refund: "/app/cards/cards",
};

export interface DashboardAlertAvailabilityOptions {
  /**
   * Whether the organization has a Bre-B key, or may create one. `null` while unknown: the listener counts it
   * as yes (a payment to a key only arrives when a key exists), Settings waits to know before offering the
   * row.
   */
  brebKeys: boolean | null;
}

/** The alerts this person can get, in catalog order. Nothing without a navigation context (no user yet). */
export function availableDashboardAlertTypes(
  ctx: NavContext | null,
  { brebKeys }: DashboardAlertAvailabilityOptions,
  registry: readonly NavEntry[] = NAVIGATION_REGISTRY,
): DashboardAlertType[] {
  if (!ctx) return [];
  return DASHBOARD_ALERT_TYPES.filter((type) => {
    if (type === "payins_breb_key" && brebKeys === false) return false;
    const entry = findEntryByTo(DASHBOARD_ALERT_PAGE[type], registry);
    return entry !== undefined && isEntryVisible(entry, ctx);
  });
}
